import { useEffect, useState } from "react";
import { Globe, MapPin } from "lucide-react";
import api from "../../lib/api";

const FLAG = {
  IN: "🇮🇳", AE: "🇦🇪", US: "🇺🇸", GB: "🇬🇧", SA: "🇸🇦", FR: "🇫🇷",
  DE: "🇩🇪", IT: "🇮🇹", ES: "🇪🇸", JP: "🇯🇵", CN: "🇨🇳", AU: "🇦🇺",
  CA: "🇨🇦", BR: "🇧🇷", RU: "🇷🇺", CH: "🇨🇭", SG: "🇸🇬", HK: "🇭🇰",
  KW: "🇰🇼", QA: "🇶🇦", OM: "🇴🇲", BH: "🇧🇭", TR: "🇹🇷", EG: "🇪🇬",
};

const NAMES = {
  IN: "India", AE: "United Arab Emirates", US: "United States", GB: "United Kingdom",
  SA: "Saudi Arabia", FR: "France", DE: "Germany", IT: "Italy", ES: "Spain",
  JP: "Japan", CN: "China", AU: "Australia", CA: "Canada", BR: "Brazil",
  RU: "Russia", CH: "Switzerland", SG: "Singapore", HK: "Hong Kong",
  KW: "Kuwait", QA: "Qatar", OM: "Oman", BH: "Bahrain", TR: "Turkey", EG: "Egypt",
};

export default function GeoStats() {
  const [d, setD] = useState({ by_country: [], by_orders: [], total_countries: 0 });

  useEffect(() => { api.get("/admin/geo-stats").then((r) => setD(r.data)).catch(() => {}); }, []);

  const max = Math.max(1, ...(d.by_country || []).map((c) => c.user_count || 0));
  const orderMax = Math.max(1, ...(d.by_orders || []).map((c) => c.orders || 0));

  const countryName = (code) => NAMES[code] || code;
  const flag = (code) => FLAG[code] || "🌍";

  return (
    <div className="card-luxe p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe size={18} className="text-copper"/>
          <div>
            <div className="overline">Geo Distribution</div>
            <div className="text-xs text-white/50 mt-0.5">Where your users sign in from + where orders ship to. <span className="text-copper">{d.total_countries}</span> countries.</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Users by login country */}
        <div>
          <div className="overline mb-3 text-white/60">Users by Login Country</div>
          <div className="space-y-2.5" data-testid="geo-users">
            {(d.by_country || []).slice(0, 12).map((c) => {
              const w = ((c.user_count / max) * 100).toFixed(1);
              return (
                <div key={c.country} className="flex items-center gap-3 text-sm" data-testid={`geo-user-${c.country}`}>
                  <span className="text-lg w-6 text-center">{flag(c.country)}</span>
                  <span className="text-white/85 w-32 truncate">{countryName(c.country)}</span>
                  <div className="flex-1 h-2 bg-white/5 relative overflow-hidden">
                    <div className="h-full bg-copper transition-all" style={{ width: `${w}%` }}/>
                  </div>
                  <span className="text-copper text-xs w-10 text-right">{c.user_count}</span>
                </div>
              );
            })}
            {(d.by_country || []).length === 0 && <div className="text-white/40 text-sm py-4 text-center">No login data yet</div>}
          </div>
        </div>

        {/* Orders by shipping country */}
        <div>
          <div className="overline mb-3 text-white/60">Orders by Shipping Country</div>
          <div className="space-y-2.5">
            {(d.by_orders || []).slice(0, 12).map((c) => {
              const w = ((c.orders / orderMax) * 100).toFixed(1);
              const looks = (c.country || "").toUpperCase();
              return (
                <div key={c.country} className="flex items-center gap-3 text-sm" data-testid={`geo-order-${c.country}`}>
                  <span className="text-lg w-6 text-center">{flag(looks.slice(0, 2)) || <MapPin size={14} className="text-white/40"/>}</span>
                  <span className="text-white/85 w-32 truncate">{c.country}</span>
                  <div className="flex-1 h-2 bg-white/5 relative overflow-hidden">
                    <div className="h-full bg-[#32D74B] transition-all" style={{ width: `${w}%` }}/>
                  </div>
                  <span className="text-[#32D74B] text-xs w-16 text-right">{c.orders} · ${c.revenue ? c.revenue.toLocaleString() : 0}</span>
                </div>
              );
            })}
            {(d.by_orders || []).length === 0 && <div className="text-white/40 text-sm py-4 text-center">No orders yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
